import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from 'xlsx';
import { saveAs } from "file-saver";
import { frenchDateOnly } from "../../../variables/functions";
import { displayFloorNum, displayNum } from "../../../variables/numbersFunctions";

export const generateSinistresSurvenusPDF = (data, categorieCode, date) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

    const now = new Date();
    const formattedNow = now.toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
    });

    const headers = [
        "N° Sinistre", "Nature", "N° Police", "Imm.", "Date Survenance", "Date Déclaration",
        "Lieu", "Assuré", "Ev. Ouv.", "Ev. Cour.", "SAP", "Cumul Reg.", "Statut"
    ];

    const rows = data.map(item => ([
        item?.numeroSinistrem || item?.numeroSinistreP || item?.numSinistre || "",
        item?.nature || "",
        item?.numeroPoliceM || item?.numeroPoliceP || "",
        item?.immatriculation || "",
        item?.dateSinistre ? frenchDateOnly(item.dateSinistre) : "",
        item?.dateDeclaration ? frenchDateOnly(item.dateDeclaration) : "",
        item?.lieu || "",
        item?.prenom ? `${item.nom} ${item.prenom}` : item.nom || "",
        displayNum(item.evaluationOuverture || 0),
        displayNum(item.evaluation || 0),
        displayNum(item.sap || 0),
        displayNum(item.cumulReglements || 0),
        item?.etat === "I" ? "INSTANCE" : item?.etat === "T" ? "TERMINÉ" : "SANS SUITE"
    ]));

    autoTable(doc, {
        head: [headers],
        body: rows,
        theme: "striped",
        styles: {
            fontSize: 8,
            lineWidth: 0.1,
            lineColor: [0, 0, 0]
        },
        margin: { top: 40 },
        bodyStyles: {
            lineWidth: 0.1,
            lineColor: [0, 0, 0],
            fillColor: [255, 255, 255], // CCFFFF
            textColor: 20,
        },
        columnStyles: {
            0: { cellWidth: 26 },
            1: { cellWidth: 19 },
            2: { cellWidth: 28 },
            3: { cellWidth: 20 },
            4: { cellWidth: 20 },
            5: { cellWidth: 20 },
            6: { cellWidth: 25 },
            7: { cellWidth: 30 },
            8: { cellWidth: 20, halign: 'right' },
            9: { cellWidth: 20, halign: 'right' },
            10: { cellWidth: 20, halign: 'right' },
            11: { cellWidth: 20, halign: 'right' },
            12: { cellWidth: 18 }
        },
        didDrawPage: (data) => {
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const pageCount = doc.internal.getNumberOfPages();
            const currentPage = doc.internal.getCurrentPageInfo().pageNumber;

            doc.setFontSize(16).setFont("helvetica", "bold").setTextColor(0, 0, 255);
            doc.text("POOL TPVM-VT GIE", 14, 15);

            doc.setFontSize(12).setFont("helvetica", "normal").setTextColor(0, 0, 0);
            doc.text(`Catégorie : ${categorieCode}`, pageWidth - 60, 15);

            doc.setFontSize(14).setFont("helvetica", "bold");
            doc.text(`ÉTAT DES SINISTRES SURVENUS DU ${frenchDateOnly(date[0])} AU ${frenchDateOnly(date[1])}`, pageWidth / 2, 25, { align: "center" });

            doc.setLineWidth(0.5);
            doc.line(14, 30, pageWidth - 14, 30);

            doc.setFontSize(10);
            doc.text(`Généré le : ${formattedNow} par PoolIns`, 14, pageHeight - 10);

            doc.setFontSize(9);
            doc.text(`Page ${currentPage} / ${pageCount}`, pageWidth - 20, pageHeight - 10);
        }
    });

    return doc.output("datauristring");
};

export const generateSinistresSurvenusExcel = (data, date) => {
    const headers = [
        "N° Sinistre", "Nature", "N° Police", "Immatriculation", "Date Survenance", "Date Déclaration",
        "Lieu", "Assuré", "Tiers", "Ev. Ouv.", "Ev. Cour.", "SAP", "Cumul Reg.", "Statut"
    ];

    const rows = data.map(item => ([
        item?.numeroSinistrem || item?.numeroSinistreP || item?.numSinistre || "",
        item?.nature || "",
        item?.numeroPoliceM || item?.numeroPoliceP || "",
        item?.immatriculation || "",
        item?.dateSinistre ? frenchDateOnly(item.dateSinistre) : "",
        item?.dateDeclaration ? frenchDateOnly(item.dateDeclaration) : "",
        item?.lieu || "",
        item?.prenom ? `${item.nom} ${item.prenom}` : item.nom || "",
        item?.tiers || "",
        item?.evaluationOuverture || 0,
        item?.evaluation || 0,
        item?.sap || 0,
        item?.cumulReglements || 0,
        item?.etat === "I" ? "INSTANCE" : item?.etat === "T" ? "TERMINÉ" : "SANS SUITE"
    ]));

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sinistres Survenus");

    const now = new Date();
    const formattedNow = now.toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
    });

    const blob = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([blob], { type: "application/octet-stream" }), `SinistresSurvenusDu${frenchDateOnly(date[0])}_au_${frenchDateOnly(date[1])}_${formattedNow}.xlsx`);
};

export const generateSinistresDeclaresPDF = (data, categorieCode, date) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

    const now = new Date();
    const formattedNow = now.toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
    });

    const headers = [
        "N° Sinistre", "Nature", "N° Police", "Imm.", "Date Survenance", "Date Déclaration",
        "Lieu", "Assuré", "Ev. Ouv.", "Ev. Cour.", "SAP", "Cumul Reg.", "Statut"
    ];

    const rows = data?.map(item => ([
        item?.numeroSinistrem || item?.numeroSinistreP || item?.numSinistre || "",
        item?.nature || "",
        item?.numeroPoliceM || item?.numeroPoliceP || "",
        item?.immatriculation || "",
        item?.dateSinistre ? frenchDateOnly(item.dateSinistre) : "",
        item?.dateDeclaration ? frenchDateOnly(item.dateDeclaration) : "",
        item?.lieu || "",
        item?.prenom ? `${item?.nom} ${item?.prenom}` : item?.nom || "",
        displayNum(item?.evaluationOuverture || 0),
        displayNum(item?.evaluation || 0),
        displayNum(item?.sap || 0),
        displayNum(item?.cumulReglements || 0),
        item?.etat === "I" ? "INSTANCE" : item?.etat === "T" ? "TERMINÉ" : "SANS SUITE"
    ]));

    autoTable(doc, {
        head: [headers],
        body: rows,
        theme: "striped",
        styles: { fontSize: 8, cellPadding: 2 },
        margin: { top: 40 },
        columnStyles: {
            0: { cellWidth: 26 },
            1: { cellWidth: 19 },
            2: { cellWidth: 28 },
            3: { cellWidth: 20 },
            4: { cellWidth: 20 },
            5: { cellWidth: 20 },
            6: { cellWidth: 25 },
            7: { cellWidth: 30 },
            8: { cellWidth: 20, halign: 'right' },
            9: { cellWidth: 20, halign: 'right' },
            10: { cellWidth: 20, halign: 'right' },
            11: { cellWidth: 20, halign: 'right' },
            12: { cellWidth: 18 }
        },
        didDrawPage: (data) => {
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const pageCount = doc.internal.getNumberOfPages();
            const currentPage = doc.internal.getCurrentPageInfo().pageNumber;

            doc.setFontSize(16).setFont("helvetica", "bold").setTextColor(0, 0, 255);
            doc.text("POOL TPVM-VT GIE", 14, 15);

            doc.setFontSize(12).setFont("helvetica", "normal").setTextColor(0, 0, 0);
            doc.text(`Catégorie : ${categorieCode}`, pageWidth - 60, 15);

            doc.setFontSize(14).setFont("helvetica", "bold");
            doc.text(`ÉTAT DES SINISTRES DECLARÉS DU ${frenchDateOnly(date[0])} AU ${frenchDateOnly(date[1])}`, pageWidth / 2, 25, { align: "center" });

            doc.setLineWidth(0.5);
            doc.line(14, 30, pageWidth - 14, 30);

            doc.setFontSize(10);
            doc.text(`Généré le : ${formattedNow} par PoolIns`, 14, pageHeight - 10);

            doc.setFontSize(9);
            doc.text(`Page ${currentPage} / ${pageCount}`, pageWidth - 20, pageHeight - 10);
        }
    });

    return doc.output("datauristring");
};

export const generateSinistresDeclaresExcel = (data, date) => {
    const headers = [
        "N° Sinistre", "Nature", "N° Police", "Immatriculation", "Date Survenance", "Date Déclaration",
        "Lieu", "Assuré", "Tiers", "Ev. Ouv.", "Ev. Cour.", "SAP", "Cumul Reg.", "Statut"
    ];

    const rows = data.map(item => ([
        item?.numeroSinistrem || item?.numeroSinistreP || item?.numSinistre || "",
        item?.nature || "",
        item?.numeroPoliceM || item?.numeroPoliceP || "",
        item?.immatriculation || "",
        item?.dateSinistre ? frenchDateOnly(item.dateSinistre) : "",
        item?.dateDeclaration ? frenchDateOnly(item.dateDeclaration) : "",
        item?.lieu || "",
        item?.prenom ? `${item.nom} ${item.prenom}` : item.nom || "",
        item?.tiers || "",
        item?.evaluationOuverture || 0,
        item?.evaluation || 0,
        item?.sap || 0,
        item?.cumulReglements || 0,
        item?.etat === "I" ? "INSTANCE" : item?.etat === "T" ? "TERMINÉ" : "SANS SUITE"
    ]));

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sinistres Déclarés");

    const now = new Date();
    const formattedNow = now.toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
    });

    const blob = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([blob], { type: "application/octet-stream" }), `SinistresDeclaresDu${frenchDateOnly(date[0])}_au_${frenchDateOnly(date[1])}_${formattedNow}.xlsx`);
};

export const generateDecaissementsPDF = async (data, categorieCode, date) => {
    const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4"
    });

    const now = new Date();
    const formattedNow = now.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });

    // Préparer les lignes
    let total = 0;
    const rows = data.map((item, index) => {
        const montant = item.montantPaye || item.montant_global || 0;
        total += montant;
        return [
            item.dateReglement
                ? new Date(item.dateReglement).toLocaleDateString("fr-FR")
                : "",
            item.numeroSinistreM || item.numeroSinistreP || item.numSinistre,
            item.dateSinistre
                ? new Date(item.dateSinistre).toLocaleDateString("fr-FR")
                : "",
            `Chèque ${item.banque || ""} N° ${item.numeroCheque || ""} - ${item?.beneficiaire}`.trim(),
            displayFloorNum(montant),
            displayFloorNum(montant / 5),
            displayFloorNum(montant / 5),
            displayFloorNum(montant / 5),
            displayFloorNum(montant / 5),
            displayFloorNum(montant / 5),
        ];
    });
    // Ajouter la ligne de total
    rows.push([
        { content: `TOTAL`, colSpan: 4, styles: { halign: "right" } },
        displayNum(total),
        displayFloorNum(total / 5),
        displayFloorNum(total / 5),
        displayFloorNum(total / 5),
        displayFloorNum(total / 5),
        displayFloorNum(total / 5),
    ]);

    // Générer le tableau
    autoTable(doc, {
        head: [
            [
                {
                    content: "Date de règlement", rowSpan: 2, styles: {
                        halign: 'center', valign: 'middle',
                        fillColor: [204, 255, 255], // CCFFFF
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0],
                        textColor: 20
                    }
                },
                {
                    content: "N° Sinistre", rowSpan: 2, styles: {
                        halign: 'center', valign: 'middle',
                        fillColor: [204, 255, 255], // CCFFFF
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0],
                        textColor: 20
                    }
                },
                {
                    content: "Date de survenance", rowSpan: 2, styles: {
                        halign: 'center', valign: 'middle',
                        fillColor: [204, 255, 255], // CCFFFF
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0],
                        textColor: 20
                    }
                },
                {
                    content: "Justificatif de règlement", rowSpan: 2, styles: {
                        halign: 'center', valign: 'middle',
                        fillColor: [204, 255, 255], // CCFFFF
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0],
                        textColor: 20
                    }
                },
                {
                    content: "Montant réglé", rowSpan: 2, styles: {
                        halign: 'center', valign: 'middle',
                        fillColor: [204, 255, 255], // CCFFFF
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0],
                        textColor: 20
                    }
                },
                {
                    content: "Répartition sinistre", colSpan: 5, styles: {
                        halign: 'center', valign: 'middle',
                        fillColor: [204, 255, 255], // CCFFFF
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0],
                        textColor: 20
                    }
                }
            ],
            [
                "FIDELIA",
                "NSIA",
                "SANLAM",
                "GTA",
                "SUNU"
            ]
        ],
        body: rows,
        theme: "striped",
        styles: {
            fontSize: 8,
            lineWidth: 0.1,
            lineColor: [0, 0, 0]
        },
        headStyles: { fillColor: [22, 160, 133] },
        bodyStyles: {
            lineWidth: 0.1,
            lineColor: [0, 0, 0],
            fillColor: [255, 255, 255], // CCFFFF
            textColor: 20,
        },
        margin: { top: 40 },
        columnStyles: {
            0: { halign: 'center', cellWidth: 18 }, // Date de réglement
            1: { halign: 'center' }, // N° Sinistre
            2: { halign: 'center', cellWidth: 18 }, // Date de survenance
            3: { halign: 'left' }, // Justificatif de réglement
            4: { halign: 'right' }, // Montant réglé
            5: { halign: 'right' }, // FIDELIA
            6: { halign: 'right' }, // NSIA
            7: { halign: 'right' }, // SANLAM
            8: { halign: 'right' }, // GTA
            9: { halign: 'right' }  // SUNU
        },
        didDrawPage: (data) => {
            const pageCount = doc.internal.getNumberOfPages();
            const pageSize = doc.internal.pageSize;
            const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
            const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();

            doc.setFontSize(16);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(0, 0, 255);
            doc.text("POOL TPVM-VT GIE", 14, 15);

            doc.setFontSize(12);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(0, 0, 0);
            doc.text(`Catégorie : ${categorieCode}`, pageWidth - 60, 15);

            doc.setFontSize(14);
            doc.setFont("helvetica", "bold");
            doc.text(
                `ÉTAT DES SINISTRES PAYÉS DU ${frenchDateOnly(date[0])} AU ${frenchDateOnly(date[1])}`,
                pageWidth / 2,
                25,
                { align: "center" }
            );

            doc.setLineWidth(0.5);
            doc.line(14, 30, pageWidth - 14, 30); // Ligne de séparation décorative


            doc.setFontSize(10);
            doc.text(`Généré le : ${formattedNow} par PoolIns`, 14, pageHeight - 10);

            const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;
            const footerText = `Page ${pageCurrent} / ${pageCount}`;

            doc.setFontSize(9);
            doc.text(footerText, pageWidth - 20, pageHeight - 10);
        }
    });


    // doc.save(`Etats des Sinistres du ${frenchDateOnly(date[0])} au ${frenchDateOnly(date[1])} _ ${formattedNow}.pdf`);
    return doc.output('datauristring');
};

export const generateDecaissementsExcel = async (data, codeCategorie, date) => {

    const wsData = [];
    let total = 0;

    // En-tête du tableau
    wsData.push(
        ["POOL TPVM-VT GIE"],
        [
            `ÉTAT DES SINISTRES PAYÉS DU ${frenchDateOnly(date[0])} AU ${frenchDateOnly(date[1])}`
        ],
        [`Catégorie : ${codeCategorie}`],
        [""], // Ligne vide
        [
            "Date de règlement",
            "N° Sinistre",
            "Date de survenance",
            "Justificatif de règlement",
            "Montant réglé",
            "FIDELIA",
            "NSIA",
            "SANLAM",
            "GTA",
            "SUNU"
        ]);

    // Contenu
    data.forEach((item) => {
        const montant = item.montantPaye || item.montant_global || 0;
        total += montant;

        const repartition = Array(5).fill(montant / 5); // réparti également

        wsData.push([
            item.dateReglement
                ? new Date(item.dateReglement).toLocaleDateString("fr-FR")
                : "",
            item.numeroSinistreM || item.numeroSinistreP || item.numSinistre || "",
            item.dateSinistre
                ? new Date(item.dateSinistre).toLocaleDateString("fr-FR")
                : "",
            `Chèque ${item.banque || ""} N° ${item.numeroCheque || ""}`.trim(),
            montant,
            ...repartition
        ]);
    });

    // Ligne TOTAL
    const totalRow = [
        "", "", "", "TOTAL",
        total,
        total / 5,
        total / 5,
        total / 5,
        total / 5,
        total / 5
    ];
    wsData.push(totalRow);

    // Créer la feuille Excel
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Mise en forme : alignement à droite pour montants
    const columnStyles = [
        {}, {}, {}, {},
        { alignment: { horizontal: "right" } },
        { alignment: { horizontal: "right" } },
        { alignment: { horizontal: "right" } },
        { alignment: { horizontal: "right" } },
        { alignment: { horizontal: "right" } },
        { alignment: { horizontal: "right" } }
    ];

    Object.keys(ws).forEach((cell) => {
        if (!cell.startsWith("!")) {
            const colIdx = XLSX.utils.decode_cell(cell).c;
            if (columnStyles[colIdx]) {
                ws[cell].s = columnStyles[colIdx];
            }
        }
    });

    XLSX.utils.book_append_sheet(wb, ws, "Décaissements");

    const now = new Date();
    const formattedNow = now.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });

    const blob = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
        cellStyles: true
    });

    saveAs(
        new Blob([blob], { type: "application/octet-stream" }),
        `Etat_Sinistres_Du_${frenchDateOnly(date[0])}_au_${frenchDateOnly(date[1])}_${formattedNow}.xlsx`
    );

};

export const generateDecaissementsHonorairesPDFOld = async (data, categorieCode, date) => {
    const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4"
    });

    const now = new Date();
    const formattedNow = now.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });

    // Grouper par bénéficiaire (toUpperCase)
    const grouped = data.reduce((acc, curr) => {
        const key = (curr.beneficiaire || 'INCONNU').toUpperCase();
        if (!acc[key]) acc[key] = [];
        acc[key].push(curr);
        return acc;
    }, {});

    console.log("grouped", grouped);

    const frenchDateOnly = (date) =>
        new Date(date).toLocaleDateString("fr-FR");

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight()

    // 🔵 EN-TÊTE UNIQUE
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 255);
    doc.text("POOL TPVM-VT GIE", 14, 15);

    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(
        `ÉTAT DES RETENUES SUR HONORAIRES PAYÉS DU ${frenchDateOnly(date[0])} AU ${frenchDateOnly(date[1])}`,
        pageWidth / 2,
        25,
        { align: "center" }
    );

    doc.setLineWidth(0.5);
    doc.line(14, 30, pageWidth - 14, 30);

    let currentY = 35; // Position après entête

    // 🔁 POUR CHAQUE BÉNÉFICIAIRE
    for (const [beneficiaire, decaissements] of Object.entries(grouped)) {
        // Saut de page si nécessaire
        if (currentY > pageHeight - 60) {
            doc.addPage();
            currentY = 20;
        }
        // Nom du bénéficiaire
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text(beneficiaire, 14, currentY);

        console.log("decaissements", decaissements);
        console.log("beneficiaire", beneficiaire);

        let total = 0;
        const rows = decaissements.map((item) => {
            const montant = item.retenue || 0;
            total += montant;
            return [
                item.dateReglement
                    ? new Date(item.dateReglement).toLocaleDateString("fr-FR")
                    : "",
                item.numeroSinistreM || item.numeroSinistreP || item.numSinistre,
                item.motifReglement || "",
                displayNum(montant)
            ];
        });

        // Ligne de total par groupe
        rows.push([
            { content: `TOTAL`, colSpan: 3, styles: { halign: "right", fontStyle: 'bold' } },
            displayNum(total)
        ]);

        const result = autoTable(doc, {
            head: [["Date règlement", "N° sinistre", "Motif règlement", "Montant"]],
            body: rows,
            // startY: currentY + 15 || 50,
            theme: "grid",
            styles: {
                fontSize: 8,
                halign: 'left'
            },
            columnStyles: {
                0: { halign: 'center', cellWidth: 35 },
                1: { halign: 'center', cellWidth: 35 },
                2: { halign: 'left', cellWidth: 100 },
                3: { halign: 'right', cellWidth: 25 }
            },
            margin: { left: 14, right: 14 },
            pageBreak: 'auto',
        });
        console.log("result", result)
        // currentY = result.finalY + 10;

        if (result && result.finalY) {
            currentY = result.finalY + 10;
        } else {
            currentY += 40; // valeur par défaut si le tableau n'est pas rendu
        }

    }

    // 🔚 Pied de page (ajouté sur la dernière page uniquement)
    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Généré le : ${formattedNow} par PoolIns`, 14, pageHeight - 10);
    doc.setFontSize(9);
    doc.text(`Page ${pageCount} / ${pageCount}`, pageWidth - 20, pageHeight - 10);

    return doc.output('datauristring');
};

const drawRHPHeader = (doc, pageWidth, formattedDateTime, date) => {
    console.log("Datttte: ", date);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 255); // Bleu
    doc.text("POOL TPVM-VT GIE", 14, 11);

    // Exemple simple de header
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0); // Bleu
    doc.text(`ÉTAT DES RETENUES SUR HONORAIRES PAYÉS DU ${frenchDateOnly(date[0])} AU ${frenchDateOnly(date[1])}`,
        pageWidth / 2,
        25,
        { align: "center" });
    doc.line(14, 12, pageWidth - 14, 12); // Ligne sous l'en-tête
};

const drawRHPFooter = (doc, formattedDateTime) => {
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setTextColor(150);
        doc.text(
            `Page ${i} sur ${pageCount} | Généré le : ${formattedDateTime}`,
            doc.internal.pageSize.getWidth() / 2,
            doc.internal.pageSize.height - 7,
            { align: 'center' }
        );
    }
};

export const generateDecaissementsHonorairesPDF = async (data, categorieCode, date) => {
    const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4"
    });

    const now = new Date();
    const formattedDateTime = now.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    drawRHPHeader(doc, pageWidth, formattedDateTime, date);

    let initialY = 20;

    // 1. Groupement des données par nom_beneficiaire
    const groupedDecaissements = {};
    data.forEach((item) => {
        const beneficiaireNom = item.beneficiaire || 'INCONNU'; // Gérer les noms nuls
        if (!groupedDecaissements[beneficiaireNom]) {
            groupedDecaissements[beneficiaireNom] = [];
        }
        groupedDecaissements[beneficiaireNom].push(item);
    });

    // Trier les bénéficiaires par nom
    const sortedBeneficiaires = Object.keys(groupedDecaissements).sort();

    // En-têtes du tableau
    const tableHeaders = [
        "Date Reglement",
        "N° Sinistre",
        "Motif Reglement",
        "Montant",
    ];

    // Styles généraux pour le tableau
    const defaultTableStyles = {
        headStyles: {
            fillColor: [204, 255, 255], // CCFFFF
            fontSize: 8,
            halign: 'center',
            lineWidth: 0.1,
            lineColor: [0, 0, 0],
            textColor: 20
        },
        styles: {
            fontSize: 7.5,
            cellPadding: 2,
            overflow: 'linebreak',
            valign: 'middle',
            lineWidth: 0.1,
            lineColor: [0, 0, 0]
        },
        columnStyles: {
            // Aligner les montants à droite
            3: { halign: 'right' }, // Montant
            4: { halign: 'right' }  // Retenue
        },
        bodyStyles: {
            lineWidth: 0.1,
            lineColor: [0, 0, 0]
        },
        margin: { top: 10, bottom: 10 }
    };

    // Parcourir chaque groupe de bénéficiaires
    sortedBeneficiaires.forEach((beneficiaireNom) => {
        const decaissementsForBeneficiaire = groupedDecaissements[beneficiaireNom];
        console.log(decaissementsForBeneficiaire);
        // Vérifier si la page est pleine avant d'ajouter le nouveau groupe
        let yPos = doc.lastAutoTable?.finalY || initialY;
        if (yPos + 20 > doc.internal.pageSize.height - 30) { // Espace suffisant pour titre + début tableau
            doc.addPage();
            drawRHPHeader(doc, pageWidth, formattedDateTime, date);
            yPos = 20; // Réinitialiser la position Y pour la nouvelle page
        }

        // Afficher le nom du bénéficiaire en majuscules
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold'); // Mettre en gras
        doc.text(beneficiaireNom.toUpperCase(), 14, yPos + 10);
        doc.setFont(undefined, 'normal'); // Revenir à la normale
        yPos += 15; // Espace après le nom du bénéficiaire

        // Préparer les lignes de données pour autoTable
        let totalMontant = 0;
        let totalRetenue = 0;

        const dataRows = decaissementsForBeneficiaire.map((d, id) => {
            const montant = Math.trunc(d.montant || 0);
            const retenue = Math.trunc(d.retenue || 0); // Utiliser 'retenue' de la fonction SQL

            totalMontant += montant;
            totalRetenue += retenue;

            return [
                d.dateReglement ? new Date(d.dateReglement).toLocaleDateString("fr-FR") : "",
                d.numeroSinistreM || d.numeroSinistreP, // Utiliser l'un ou l'autre
                d.motifReglement,
                new Intl.NumberFormat("fr-FR").format(retenue).replace(/\s/g, ' ')
            ];
        });

        // Ajouter la ligne de total pour ce bénéficiaire
        const totalRow = [
            { content: 'TOTAL', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold', fillColor: [230, 255, 230] } },
            { content: new Intl.NumberFormat("fr-FR").format(totalRetenue).replace(/\s/g, ' '), styles: { halign: 'right', fontStyle: 'bold', fillColor: [230, 255, 230] } }
        ];
        dataRows.push(totalRow);
        console.log(totalMontant);


        // Générer le tableau pour le groupe actuel
        autoTable(doc, {
            startY: yPos,
            head: [tableHeaders],
            body: dataRows,
            ...defaultTableStyles, // Appliquer les styles par défaut
            didParseCell: (data) => {
                // Si c'est la ligne de total, appliquer un style spécifique aux cellules
                if (data.row.index === dataRows.length - 1) { // Dernière ligne (le total)
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [230, 255, 230]; // Vert clair pour les totaux
                    if (data.column.index >= 3) { // Montant et Retenue
                        data.cell.styles.halign = 'right';
                    }
                }
            }
        });
    });

    drawRHPFooter(doc, formattedDateTime);
    return doc.output('datauristring');
};

export const generateRatioSP = async (data, date) => {
    const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4"
    });

    const now = new Date();
    const formattedNow = now.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });

    // Préparer les lignes
    let nombreContrats = 0;
    let primesTotales = 0;
    let nombreSinistres = 0;
    let montantSinistres = 0;
    const rows = data.map((item, index) => {
        nombreContrats += item.nombreContrats || 0;
        primesTotales += item.primesTotales || 0;
        nombreSinistres += item.nombreSinistres || 0;
        montantSinistres += item.montantSinistres || 0;
        return [
            item.categorie,
            displayNum(item.nombreContrats),
            displayNum(item.primesTotales),
            displayNum(item.nombreSinistres),
            displayNum(item.montantSinistres),
            item.ratioSP,
        ];
    });
    // Ajouter la ligne de total
    rows.push([
        { content: `TOTAL`, styles: { halign: "right" } },
        displayNum(nombreContrats),
        displayNum(Math.floor(primesTotales)),
        displayNum(nombreSinistres),
        displayNum(montantSinistres),
        displayNum(Math.floor((montantSinistres / primesTotales) * 100)),
    ]);

    // Générer le tableau
    autoTable(doc, {
        head: [
            ["Catégorie",
                "Nombre de contrats",
                "Primes encaissées (P)",
                "Nombre de sinistres",
                "Montant des sinistres (S)",
                "Ratio SP"],
        ],
        body: rows,
        theme: "striped",
        styles: {
            fontSize: 8,
            lineWidth: 0.1,
            lineColor: [0, 0, 0]
        },
        headStyles: { fillColor: [22, 160, 133] },
        bodyStyles: {
            lineWidth: 0.1,
            lineColor: [0, 0, 0],
            fillColor: [255, 255, 255], // CCFFFF
            textColor: 20,
        },
        margin: { top: 40 },
        columnStyles: {
            0: { halign: 'left' },
            1: { halign: 'center' },
            2: { halign: 'right' },
            3: { halign: 'center' },
            4: { halign: 'right' },
            5: { halign: 'right' },
        },
        didDrawPage: (data) => {
            const pageCount = doc.internal.getNumberOfPages();
            const pageSize = doc.internal.pageSize;
            const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
            const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();

            doc.setFontSize(16);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(0, 0, 255);
            doc.text("POOL TPVM-VT GIE", 14, 15);

            doc.setFontSize(14);
            doc.setFont("helvetica", "bold");
            doc.text(
                `RATIO S/P PERIODE DU ${frenchDateOnly(date[0])} AU ${frenchDateOnly(date[1])}`,
                pageWidth / 2,
                25,
                { align: "center" }
            );

            doc.setLineWidth(0.5);
            doc.line(14, 30, pageWidth - 14, 30); // Ligne de séparation décorative


            doc.setFontSize(10);
            doc.text(`Généré le : ${formattedNow} par PoolIns`, 14, pageHeight - 10);

            const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;
            const footerText = `Page ${pageCurrent} / ${pageCount}`;

            doc.setFontSize(9);
            doc.text(footerText, pageWidth - 20, pageHeight - 10);
        }
    });


    // doc.save(`Etats des Sinistres du ${frenchDateOnly(date[0])} au ${frenchDateOnly(date[1])} _ ${formattedNow}.pdf`);
    return doc.output('datauristring');
};

export const generateBonAPayerPDF = (data, categorieCode, date) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

    const now = new Date();
    const formattedNow = now.toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
    });

    const headers = [
        "Bénéficiaire", "Montant à Payer", "N° Sinistre",
        "Motif Règlement", "Date Ordre de dépense"
    ];

    let total = 0;
    const rows = data.map(item => {
        const montant = item.montantPaye || 0;
        total += montant;
        return [
            item.beneficiaire || "",
            displayNum(montant),
            item.numeroSinistreM || item.numeroSinistreP,
            item.motifReglement || "",
            item.dateReglement ? frenchDateOnly(item.dateReglement) : "",
        ];
    });

    // Ligne de total
    rows.push([
        { content: "TOTAL", colSpan: 3, styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(total), styles: { fontStyle: "bold" } },
        "", ""
    ]);

    autoTable(doc, {
        head: [headers],
        body: rows,
        theme: "striped",
        styles: {
            fontSize: 8,
            lineWidth: 0.1,
            lineColor: [0, 0, 0]
        },
        headStyles: { fillColor: [22, 160, 133] },
        bodyStyles: {
            lineWidth: 0.1,
            lineColor: [0, 0, 0],
            fillColor: [255, 255, 255],
            textColor: 20,
        },
        margin: { top: 40 },
        columnStyles: {
            0: { cellWidth: 35, halign: "center" },
            1: { cellWidth: 35, halign: "center" },
            2: { cellWidth: 30, halign: "center" },
            3: { cellWidth: 35, halign: "right" },
            4: { cellWidth: 60, halign: "left" },
            5: { cellWidth: 60, halign: "left" }
        },
        didDrawPage: () => {
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const pageCount = doc.internal.getNumberOfPages();
            const currentPage = doc.internal.getCurrentPageInfo().pageNumber;

            doc.setFontSize(16).setFont("helvetica", "bold").setTextColor(0, 0, 255);
            doc.text("POOL TPVM-VT GIE", 14, 15);

            doc.setFontSize(12).setFont("helvetica", "normal").setTextColor(0, 0, 0);
            doc.text(`Catégorie : ${categorieCode}`, pageWidth - 60, 15);

            doc.setFontSize(14).setFont("helvetica", "bold");
            doc.text(
                `ÉTAT DES SINISTRES BON À PAYER DU ${frenchDateOnly(date[0])} AU ${frenchDateOnly(date[1])}`,
                pageWidth / 2, 25, { align: "center" }
            );

            doc.setLineWidth(0.5);
            doc.line(14, 30, pageWidth - 14, 30);

            doc.setFontSize(10);
            doc.text(`Généré le : ${formattedNow} par PoolIns`, 14, pageHeight - 10);

            doc.setFontSize(9);
            doc.text(`Page ${currentPage} / ${pageCount}`, pageWidth - 20, pageHeight - 10);
        }
    });

    return doc.output("datauristring");
};

export const generateBonAPayerExcel = (data, codeCategorie, date) => {
    const wsData = [];
    let total = 0;
    console.log("date:", date);
    wsData.push(
        ["POOL TPVM-VT GIE"],
        [`ÉTAT DES SINISTRES BON À PAYER DU ${frenchDateOnly(date[0])} AU ${frenchDateOnly(date[1])}`],
        [`Catégorie : ${codeCategorie}`],
        [""],
        [
            "Bénéficiaire", "Montant à Payer", "N° Sinistre", "Motif Règlement", "Date Ordre de dépense",
        ]
    );

    data.forEach(item => {
        const montant = item.montantPaye || 0;
        total += montant;
        wsData.push([
            item.beneficiaire || "",
            displayNum(montant),
            item.numeroSinistreM || item.numeroSinistreP,
            item.motifReglement || "",
            item.dateReglement ? frenchDateOnly(item.dateReglement) : "",
        ]);
    });

    wsData.push(["", "", "TOTAL", total, "", ""]);

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bon à Payer");

    const now = new Date();
    const formattedNow = now.toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
    });

    const blob = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
        new Blob([blob], { type: "application/octet-stream" }),
        `Etat_Sinistres_Bon_A_Payer_Du_${frenchDateOnly(date[0])}_au_${frenchDateOnly(date[1])}_${formattedNow}.xlsx`
    );
};
